import Vue from 'vue';
import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import WorkflowsAPI from '../../api/workflows';

// Every key is declared here, including blockTypes and each ui flag: Vue 2 only makes what it
// walks at store creation reactive, so a key added later would update silently and render never.
export const state = {
  records: [],
  blockTypes: {},
  // The attributes a condition block may test, with the operators each one accepts. Served
  // beside the catalogue because both come from the server, and neither may be restated here.
  conditionAttributes: [],
  // AI build credits left on this account. null, not 0: nobody has asked yet, which is not the
  // same fact as having nothing left, and the panel says something different for each.
  credits: null,
  // When set, the monthly ceiling does not apply to this person until then, and `credits` is
  // null — which is why the two are separate. Read together they distinguish "nobody has asked
  // yet" from "the limit is off today", and both of those from "you have none left".
  unlimitedUntil: null,
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isPublishing: false,
    isFetchingBlocks: false,
    isBuilding: false,
  },
};

export const getters = {
  getWorkflows($state) {
    return $state.records;
  },
  // Route params arrive as strings; ids off the API are numbers. Coerce or the editor opens
  // empty on a workflow that is sitting right there in the store.
  getWorkflow: $state => id => {
    return $state.records.find(record => record.id === Number(id));
  },
  getBlockTypes($state) {
    return $state.blockTypes;
  },
  getConditionAttributes($state) {
    return $state.conditionAttributes;
  },
  getCredits($state) {
    return $state.credits;
  },
  getUnlimitedUntil($state) {
    return $state.unlimitedUntil;
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
};

export const actions = {
  get: async function getWorkflows({ commit }) {
    commit(types.SET_WORKFLOW_UI_FLAG, { isFetching: true });
    try {
      const response = await WorkflowsAPI.get();
      commit(types.SET_WORKFLOWS, response.data.payload);
    } finally {
      commit(types.SET_WORKFLOW_UI_FLAG, { isFetching: false });
    }
  },
  // The index response omits both graphs, so the record this replaces is deliberately the
  // fatter one: only show carries draft_graph/published_graph for the editor to open.
  show: async function showWorkflow({ commit }, id) {
    commit(types.SET_WORKFLOW_UI_FLAG, { isFetchingItem: true });
    try {
      const response = await WorkflowsAPI.show(id);
      commit(types.SET_WORKFLOW_ITEM, response.data.payload);
    } finally {
      commit(types.SET_WORKFLOW_UI_FLAG, { isFetchingItem: false });
    }
  },
  create: async function createWorkflow({ commit }, payload) {
    commit(types.SET_WORKFLOW_UI_FLAG, { isCreating: true });
    try {
      // The controller requires the root key. Sent bare this 400s as a missing parameter,
      // which reads nothing like the validation error the user was expecting.
      const response = await WorkflowsAPI.create({ workflow: payload });
      commit(types.ADD_WORKFLOW, response.data.payload);
      return response.data.payload;
    } finally {
      commit(types.SET_WORKFLOW_UI_FLAG, { isCreating: false });
    }
  },
  update: async function updateWorkflow({ commit }, { id, ...payload }) {
    commit(types.SET_WORKFLOW_UI_FLAG, { isUpdating: true });
    try {
      const response = await WorkflowsAPI.update(id, { workflow: payload });
      commit(types.EDIT_WORKFLOW, response.data.payload);
      return response.data.payload;
    } finally {
      commit(types.SET_WORKFLOW_UI_FLAG, { isUpdating: false });
    }
  },
  delete: async function deleteWorkflow({ commit }, id) {
    commit(types.SET_WORKFLOW_UI_FLAG, { isDeleting: true });
    try {
      await WorkflowsAPI.delete(id);
      // destroy() matches with !==, so a string id from a route would delete nothing and
      // report success.
      commit(types.DELETE_WORKFLOW, Number(id));
    } finally {
      commit(types.SET_WORKFLOW_UI_FLAG, { isDeleting: false });
    }
  },
  // Publishing returns the whole record — published_version and published_at both move — so the
  // row is replaced rather than patched. A rejected publish keeps its 422 body intact for the
  // caller to list; see the deliberate absence of a catch.
  publish: async function publishWorkflow({ commit }, id) {
    commit(types.SET_WORKFLOW_UI_FLAG, { isPublishing: true });
    try {
      const response = await WorkflowsAPI.publish(id);
      commit(types.EDIT_WORKFLOW, response.data.payload);
      return response.data.payload;
    } finally {
      commit(types.SET_WORKFLOW_UI_FLAG, { isPublishing: false });
    }
  },
  // Deliberately not stored: a validation is a question about a graph the user is still
  // drawing, not state. Keeping it out of the store means the answer cannot outlive the edit
  // that made it wrong.
  validate: async function validateWorkflow(_context, graph) {
    const response = await WorkflowsAPI.validateGraph(graph);
    return response.data;
  },
  /**
   * Describe a workflow, get one back. Like validate, the graph it returns is deliberately not
   * committed anywhere: it is a proposal the author has not accepted yet, and a proposal parked
   * in the store is one that outlives the screen that asked for it.
   *
   * The credit count IS committed — it is a fact about the account, true until the next build.
   */
  build: async function buildWorkflow(
    { commit },
    { prompt, graph = null } = {}
  ) {
    commit(types.SET_WORKFLOW_UI_FLAG, { isBuilding: true });
    try {
      const response = await WorkflowsAPI.build({ prompt, graph });
      commit(types.SET_WORKFLOW_CREDITS, response.data.credits_remaining);
      commit(types.SET_WORKFLOW_UNLIMITED_UNTIL, response.data.unlimited_until);
      return response.data;
    } catch (error) {
      // A 402 carries the one thing worth keeping from a failure: there is nothing left to
      // spend. Recorded so the panel can say so, rather than offering a button that cannot work.
      if (error.response?.status === 402) {
        commit(types.SET_WORKFLOW_CREDITS, 0);
      }
      // Rethrown like every other action here, so the caller can list the 422's own errors.
      throw error;
    } finally {
      commit(types.SET_WORKFLOW_UI_FLAG, { isBuilding: false });
    }
  },
  fetchBlocks: async function fetchWorkflowBlocks({ commit }) {
    commit(types.SET_WORKFLOW_UI_FLAG, { isFetchingBlocks: true });
    try {
      const response = await WorkflowsAPI.blocks();
      commit(types.SET_WORKFLOW_BLOCK_TYPES, response.data.payload);
      commit(
        types.SET_WORKFLOW_CONDITION_ATTRIBUTES,
        response.data.condition_attributes || []
      );
      // Arrives with the catalogue so the assistant can state the balance on first paint, rather
      // than only after a build has already been spent finding out what it was.
      commit(types.SET_WORKFLOW_CREDITS, response.data.credits_remaining);
      commit(types.SET_WORKFLOW_UNLIMITED_UNTIL, response.data.unlimited_until);
    } finally {
      commit(types.SET_WORKFLOW_UI_FLAG, { isFetchingBlocks: false });
    }
  },
};

export const mutations = {
  [types.SET_WORKFLOW_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.SET_WORKFLOWS]: MutationHelpers.set,
  [types.ADD_WORKFLOW]: MutationHelpers.create,
  [types.EDIT_WORKFLOW]: MutationHelpers.update,
  [types.DELETE_WORKFLOW]: MutationHelpers.destroy,
  // Upsert, because show can land either on a row the index already listed or on a deep link
  // opened cold. Hand written rather than MutationHelpers.setSingleRecord: that one assigns
  // straight into the array, which Vue 2 does not observe, and the editor would open on the
  // graphless index copy of the record.
  [types.SET_WORKFLOW_ITEM]($state, data) {
    const index = $state.records.findIndex(record => record.id === data.id);
    if (index > -1) {
      Vue.set($state.records, index, data);
    } else {
      $state.records.push(data);
    }
  },
  // The catalogue is keyed by block kind, not a list of records, so none of MutationHelpers
  // applies — every one of them writes state.records.
  [types.SET_WORKFLOW_BLOCK_TYPES]($state, data) {
    $state.blockTypes = data;
  },
  [types.SET_WORKFLOW_CONDITION_ATTRIBUTES]($state, data) {
    $state.conditionAttributes = data;
  },
  // Coerced, because an absent credits_remaining must not turn the counter into "undefined"
  // on screen — an unknown count reads as null, exactly as it starts.
  [types.SET_WORKFLOW_CREDITS]($state, value) {
    $state.credits = typeof value === 'number' ? value : null;
  },
  [types.SET_WORKFLOW_UNLIMITED_UNTIL]($state, value) {
    $state.unlimitedUntil = value || null;
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
